import * as ko from "knockout";
import {ISyncField} from "../../../index";
import {IAvailableData, TColumnsConfig, TMode} from "./index";
import {Item} from "./item";
import Abstract from "./Abstract";
import {Table} from "./table";
import {ErrorObject} from "../../../error/ErrorObject";
import {Permission} from "../../../constants/Permission";
import {_} from 'lodash';
import pubsub from "../../../pubsub";
import {nsSystemfields} from "../nsSystemfields";
import groupHtml from './group.html?raw';

export const template = groupHtml;

export class Group extends Abstract {
    private readonly isMutable = nsSystemfields.isMutable

    public readonly items: Item[] = []

    public override readonly mode = ko.observable<TMode>('read')
    private name!: KnockoutObservable<string>
    private required!: KnockoutObservable<boolean>
    private ignorefilter!: KnockoutObservable<boolean>
    private issynced!: KnockoutObservable<boolean>
    private isoverridablebylevel!: KnockoutObservable<Permission>

    private err_name!: ErrorObject
    private err_isoverridablebylevel!: ErrorObject;

    constructor(syncfield: ISyncField, availableData: IAvailableData, table: Table) {
        super(syncfield, availableData, table)
    }

    init() {
        this.name = ko.observable(String(this.syncfield.name))
        this.required = ko.observable(this.syncfield.required)
        this.ignorefilter = ko.observable(this.syncfield.ignorefilter)
        this.issynced = ko.observable(this.syncfield.issynced)
        this.isoverridablebylevel = ko.observable(this.syncfield.isoverridablebylevel)

        this.err_name = new ErrorObject()
        this.err_name.addValidator({
            fn: () => !!this.name(),
            message: 'invalid value',
        })
        this.err_name.addValidator({
            fn: () => !this.availableData.groups().find(g => {
                if (g == this) {
                    return false
                }
                const name = ko.isObservable(g.name)
                    ? g.name()
                    : g.syncfield.name
                ;
                return String(name).trim().toLowerCase() == this.name().trim().toLowerCase()
            }),
            message: 'list contains duplicates',
        })
        this.err_isoverridablebylevel = new ErrorObject()
        this.err_isoverridablebylevel.addValidator({
            fn: () => !!this.isoverridablebylevel(),
            message: 'invalid value',
        })
        this.errorManager.addErrorObject(this.err_name)
    }

    uninit() {
        this.items.forEach(o => o.uninit())
        super._uninit(this.availableData.columnsConfig())
    }

    addSyncfield(syncfield: ISyncField) {
        const index = this.availableData.selectedLanguageId().indexOf(syncfield.languageid)
        this.items[index] = new Item(syncfield, this.availableData, this.table, this)
    }

    /*******************************************************************************************************************
     * View helpers
     ******************************************************************************************************************/

    filterSearchKey(searchkey: string): boolean {
        const a = !!this.availableData.columnsConfig().find((key: keyof ISyncField) => String(this.getDef(key).content(this)).toLowerCase().includes(searchkey));
        const b = !!this.items.find(o => o.filterSearchKey(searchkey));
        return a || b
    }

    isChanged(): boolean {
        if (this.isMode('read')) {
            return false
        }
        if (this.isMode('create', 'delete')) {
            return true
        }
        // mode is 'update':
        if (!!this.items.find(o => o.isChanged())) {
            return true
        }
        const self: any = this
        return this.availableData.columnsConfig().reduce((ret: boolean, key: TColumnsConfig) => {
            if (ret) {
                return true
            }
            if (key in self) {
                const obj = self[key]
                const thisVal = ko.isObservable(obj) ? obj(): obj
                return thisVal != this.syncfield[key]
            } else {
                return ret
            }
        }, false)
    }

    getSortValue(): string | number {
        const col = this.table.sortcol()
        if (this.availableData.columnsConfig().includes(col as any)) {
            return this.getDef(col).content(this)
        } else {
            const segments = col.split('_')
            const item = this.items.find(o => o.languageid == Number(segments[1]))
            return item?.getSortValue() ?? ''
        }
    }

    /*******************************************************************************************************************
     * View interaction
     ******************************************************************************************************************/

    onUpdateClick() {
        this.init()
        this.items.forEach(o => o.init())
        this.mode('update')
    }

    onUndoClick() {
        this.mode('read')
        this.uninit()
    }

    onDeleteClick() {
        if (this.isMode('create')) {
            this.uninit()
            this.availableData.groups.remove(this)
        } else {
            this.uninit()
            this.mode('delete')
        }
    }

    /*******************************************************************************************************************
     * Misc.
     ******************************************************************************************************************/

    public getSubmitData(): ISyncField {
        const self: any = this
        const ret: ISyncField = _.cloneDeep(this.syncfield)

        this.availableData.columnsConfig().forEach((k: TColumnsConfig) => {
            if (k in self) {
                const v = self[k]
                let val = ko.isObservable(v) ? v(): v
                if (typeof val === 'string') {
                    val = val.trim()
                }
                (ret as any)[k] = val?.trim?.() ?? val
            }
        })
        return ret
    }
    
    private onDuplicateClick(group: Group) {
        pubsub.trigger('copy.systemfield', group)
    }
}
